import type { IFilter } from '@models/Filter'
import type { Query } from '@models/Query'
import { endOfDay, startOfDay } from '@util/date-util'

import { apiClient } from '../../../gateways/api'
import type {
  FetchWerklijsten,
  FetchWerklijstenFilters,
} from '../types/FetchWerklijsten'

export { fetchWerklijsten, createFilters, initialState }

const fetchWerklijsten = async ({
  query,
}: {
  query: Query<FetchWerklijstenFilters>
}): Promise<FetchWerklijsten['data']> => {
  const { filters } = query

  const { data } = await apiClient({
    url: '/rest/dashboard/hoofdleiding/werklijst',
    method: 'POST',
    data: { filters: createFilters(filters) },
  })

  return data
}

const createFilters = ({
  organisatie,
  startUitvoeringWeekTot,
  startUitvoeringWeekVan,
  fromDate,
  toDate,
  verantwoordelijke,
  due,
  processtatus,
  typeWerk,
  gebied,
  labels,
  myList,
  gemeente,
}: FetchWerklijstenFilters) => {
  const filters: IFilter[] = []

  if (organisatie)
    filters.push({
      name: 'organisatie',
      value: organisatie.label,
      operator: 'eq',
    })

  filters.push({
    name: 'isActive',
    value: true,
    operator: 'eq',
  })

  if (fromDate) {
    filters.push({
      name: 'dueDate',
      value: startOfDay(fromDate),
      operator: 'gte',
    })
  }

  if (toDate) {
    filters.push({
      name: 'dueDate',
      value: endOfDay(toDate),
      operator: 'lte',
    })
  }

  if (startUitvoeringWeekVan)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekVan,
      operator: 'gte',
    })
  if (startUitvoeringWeekTot)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekTot,
      operator: 'lte',
    })

  if (verantwoordelijke)
    filters.push({
      name: 'verantwoordelijken',
      value: verantwoordelijke.code,
      operator: 'eq',
    })

  if (due) {
    filters.push({
      name: 'due',
      value: true,
      operator: 'eq',
    })
  }

  if (processtatus && processtatus.length > 0) {
    filters.push({
      name: 'processtatus',
      value: processtatus,
      operator: 'in',
    })
    filters.push({
      name: 'searchValues.statusText',
      value: processtatus,
      operator: 'in',
    })
  }

  if (Array.isArray(typeWerk) && typeWerk.length > 0) {
    filters.push({
      name: 'searchValues.typeWerk',
      operator: 'in',
      value: typeWerk,
    })
  }
  if (gebied && gebied.length > 0)
    filters.push({
      name: 'gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })

  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })

  if (myList) {
    filters.push({ name: 'my-list', value: true, operator: 'eq' })
  }

  if (gemeente.length > 0)
    filters.push({
      name: 'searchValues.gemeente',
      value: gemeente,
      operator: 'in',
    })

  return filters
}

const initialState: FetchWerklijsten = {
  data: {
    acties: [],
    activiteiten: [],
    bijlagen: [],
    reviewBijlagen: [],
    subprocessen: [],
  },
  query: {
    filters: {
      gebied: [],
      labels: [],
      organisatie: null,
      netbeheerder: null,
      myList: false,
      startUitvoeringWeekTot: '',
      startUitvoeringWeekVan: '',
      fromDate: null,
      toDate: null,
      verantwoordelijke: null,
      due: true,
      processtatus: [],
      typeWerk: [],
      gemeente: [],
    },
  },
}
